<template>
  <div class="rangeslider-layout">
    <div class="labels">
      <transition name="fade-label">
        <span v-show="showMinLabel" class="min-label">{{ computedMinLabel }}</span>
      </transition>
      <transition name="fade-label">
        <span v-show="showMaxLabel" class="max-label">{{ computedMaxLabel }}</span>
      </transition>
    </div>
    <div class="rangeslider" ref="slider">
      <input
        type="range"
        :min="min"
        :max="max"
        v-model.number="sliderValue"
        @input="updateValue"
        ref="rangeInput"
        class="rangeslider__input"
      />
      <div class="rangeslider__track" :style="{ width: getThumbPosition() }"></div>
      <div ref="handle" class="rangeslider__handle" :style="{ left: getThumbPosition() }">
        {{ sliderValue }}
      </div>
    </div>
  </div>
</template>

<script>
import { state } from '@/lib/state'

export default {
  props: {
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    minLabel: { type: String, default: '' },
    maxLabel: { type: String, default: '' },
    stateKey: { type: String, required: true },
  },
  data() {
    return {
      sliderValue: this.min,
      labelBuffer: 1, // Hide label if within this range of min or max
    }
  },
  computed: {
    computedMinLabel() {
      return this.minLabel || this.min
    },
    computedMaxLabel() {
      return this.maxLabel || this.max
    },
    showMinLabel() {
      return this.sliderValue > this.min + this.labelBuffer
    },
    showMaxLabel() {
      return this.sliderValue < this.max - this.labelBuffer
    },
  },
  mounted() {
    const list = state.lists[state.listKeys[this.stateKey]]
    const selected = list?.find((item) => item.id === state.answers[this.stateKey])

    // If HoursAWeek we set the sliderValue startvalue to be 37 - else min value
    if (this.stateKey === 'Salary.Questions.HoursAWeek') {
      this.sliderValue = 37
    } else {
      this.sliderValue = parseFloat(selected?.text) || this.min
    }

    console.log('mounted')
    console.log(
      'state.answers[this.stateKey]  if empty we are entering the question and can freely override with member data =========> ',
      state.answers[this.stateKey],
    )

    if (state.user?.memberData[this.stateKey] && !state.answers[this.stateKey]) {
      console.log('====== member has data for this field and no state so we can override ====')
      console.log('DATA ==> ', state.user?.memberData[this.stateKey])

      this.sliderValue = parseFloat(state.user?.memberData[this.stateKey]) || this.min
    }

    // Update slider value if member data is available
    this.updateValue()

    this.$nextTick(() => {
      this.updateHandle()
      this.updateTrack()
    })

    this.emitter.on('run-updateValue', this.updateValue)
    window.addEventListener('resize', this.updateHandle)
    window.addEventListener('resize', this.updateTrack)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateHandle)
    window.removeEventListener('resize', this.updateTrack)
  },
  methods: {
    updateValue() {
      const value = this.sliderValue
      const list = state.lists[state.listKeys[this.stateKey]]
      const selectedOption = list?.find((item) => parseFloat(item.text) === value)
      if (!selectedOption?.id) {
        console.error('Could not find id for value', value)
        return
      }
      // Store the selected value's id and text
      state.answers[this.stateKey] = selectedOption.id
      state.displayAnswers[this.stateKey] = selectedOption.text
      this.$emit('input', value)
      this.updateHandle()
      this.updateTrack()
    },
    getThumbPosition() {
      if (!this.$refs.slider) return '0px'
      const rangeInput = this.$refs.rangeInput
      const value = (rangeInput.value - rangeInput.min) / (rangeInput.max - rangeInput.min)
      const thumbWidth = this.$refs.handle.offsetWidth
      const sliderWidth = this.$refs.slider.offsetWidth

      const minPos = thumbWidth / 2
      const maxPos = sliderWidth - thumbWidth / 2
      const position = minPos + value * (maxPos - minPos)

      return `${position - thumbWidth / 2}px`
    },
    updateHandle() {
      if (this.$refs.handle) {
        this.$refs.handle.style.left = this.getThumbPosition()
      }
    },
    updateTrack() {
      const track = this.$refs.slider?.querySelector('.rangeslider__track')
      if (track) {
        track.style.width = this.getThumbPosition()
      }
    },
  },
}
</script>

<style scoped>
.rangeslider-layout {
  display: flex;
  flex-direction: column;

  /* Core sizing variables */
  --track-height: 3px;
  --thumb-size-mobile: 32px;
  --thumb-size-desktop: 44px;
  --handle-offset-mobile: -8px;
  --handle-offset-desktop: -52px;
  --bottom-offset: -9px;
  --hitbox-padding: 44px; /* Makes thumb more tappable */

  @media screen and (min-width: 768px) {
    --track-height: 8px;
    --bottom-offset: -11px;
  }
}

.rangeslider {
  position: relative;
  width: 99%;
  font-family: var(--font-it-ex);
}
.rangeslider__track {
  width: 50%;
  height: var(--track-height);
  background: var(--color-white);
  position: absolute;
  bottom: var(--bottom-offset);
  border: 2px;
  left: 0;
  pointer-events: none;
}
input[type='range']:active::-webkit-slider-thumb {
  cursor: grabbing;
}

input[type='range']::-webkit-slider-runnable-track {
  background: var(--color-darkblue);
  border: 8px solid transparent;
  border: 1px solid var(--color-darkblue);
  height: 4px; /* Default height for mobile */
}

/* Firefox */
input[type='range']::-moz-range-track {
  background: var(--color-darkblue);
  border: 1px solid var(--color-darkblue);
  height: var(--track-height);
}

/* Increase track height on desktop */
@media screen and (min-width: 768px) {
  input[type='range']::-webkit-slider-runnable-track {
    height: var(--track-height);
    border: 4px solid transparent;
  }

  input[type='range']::-moz-range-track {
    height: var(--track-height);
  }
}

input[type='range']::-moz-range-progress {
  background-color: var(--color-white);
  height: var(--track-height);
}

input[type='range']::-webkit-progress-value {
  background-color: var(--color-white);
}
.rangeslider__input {
  width: 100%;
  border: var(--bottom-offset) solid transparent;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  height: 40px;
  margin-top: -13px;
  cursor: pointer;
}
.rangeslider__handle {
  position: absolute;
  top: -40px;
  color: var(--color-white);
  font-size: 16px;
  text-align: center;
  width: 32px;
  height: 32px;
  line-height: 32px;
  pointer-events: none;
  font-weight: 700;
  font-size: 20px;

  @media screen and (min-width: 768px) {
    font-size: 24px;
    top: -52px;
    width: 44px;
    height: 44px;
    line-height: 44px;
  }
}
.rangeslider__handle::before {
  content: '';
  display: block;
  position: absolute;
  top: 30px;
  width: 32px;
  border: 1px solid var(--color-white);
  height: 32px;
  background-color: white;
  border-radius: 999px;

  @media screen and (min-width: 768px) {
    top: 38px;
    width: 44px;
    height: 44px;
  }
}
.labels {
  position: relative;
  min-height: 44px;
  font-size: var(--font-size-body);
  color: var(--color-white);

  @media screen and (min-width: 768px) {
    padding-bottom: 12px;
  }
}
.min-label,
.max-label {
  font-size: var(--font-size-small);
  line-height: 32px;
  font-size: var(--font-size-body);
}

@media screen and (min-width: 768px) {
  .min-label,
  .max-label {
    font-size: 20px;
  }
}

.min-label {
  position: absolute;
  left: 0;
}

.max-label {
  position: absolute;
  right: 0;
}

.fade-label-enter-active,
.fade-label-leave-active {
  transition: opacity var(--ani-speed-2) var(--ani-type-1);
}

.fade-label-enter-from,
.fade-label-leave-to {
  opacity: 0;
}

.fade-label-enter-to,
.fade-label-leave-from {
  opacity: 1;
}
</style>
